import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';

import { CONTAS, GRUPOS } from '../config/planoCanonico';
import { formatarMoeda } from '../utils/series';

/**
 * A TELA DE CONFERENCIA — o coracao da importacao adaptativa.
 *
 * Ela existe porque um importador que decide sozinho e grava e um importador que
 * erra sozinho e grava. Neste produto um erro de classificacao nao aparece na
 * tela: ele aparece como um numero diferente, que ninguem tem como conferir.
 *
 * Duas decisoes de desenho:
 *
 * 1. QUEM DECIDE SE PRECISA DE REVISAO E A ARITMETICA, nao a confianca do
 *    classificador. Arquivo cuja soma fecha contra os totais do proprio arquivo
 *    vem recolhido, e confirmar e um clique. Arquivo que nao fecha abre so as
 *    linhas problematicas. O esforco fica proporcional a bagunca.
 *
 * 2. O QUE FICOU DE FORA APARECE SOMADO E NOMEADO. Sumir calado com o dinheiro
 *    de uma linha e o modo de falha que este produto menos aceita.
 */

const COR_DA_CONFIANCA = {
  alta: 'success',
  media: 'warning',
  baixa: 'default'
};

const ROTULO_DA_CONFIANCA = {
  alta: 'reconhecida',
  media: 'provavel',
  baixa: 'incerta'
};

/** As contas que o usuario pode escolher, agrupadas por bloco da DRE. */
const opcoesDeConta = () => {
  const porBloco = GRUPOS.map((grupo) => ({
    grupo,
    contas: CONTAS.filter((c) => c.legado && String(c.codigo).split('.')[0] === grupo.codigo)
  })).filter((b) => b.contas.length);

  const itens = [];
  porBloco.forEach(({ grupo, contas }) => {
    itens.push({ separador: true, rotulo: grupo.nome });
    contas.forEach((conta) => itens.push({ conta }));
  });
  return itens;
};

function ConferenciaImportacao({ proposta, onConfirmar, onCancelar, enviando = false }) {
  const [confirmacoes, setConfirmacoes] = useState({});
  const [mostrarTudo, setMostrarTudo] = useState(false);

  const opcoes = useMemo(opcoesDeConta, []);

  const contaDaLinha = (linha) => {
    if (Object.prototype.hasOwnProperty.call(confirmacoes, linha.indice)) {
      return confirmacoes[linha.indice] || '';
    }
    return linha.conta ? linha.conta.codigo : '';
  };

  const linhasDeConta = (proposta.linhas || []).filter((l) => !l.ehTotal);
  const precisamDeOlho = linhasDeConta.filter((l) => !contaDaLinha(l) || l.confianca !== 'alta');
  const visiveis = mostrarTudo ? linhasDeConta : precisamDeOlho;

  const foraDaConta = linhasDeConta.filter((l) => !contaDaLinha(l));
  const totalDeFora = (proposta.periodos || []).map((_, i) =>
    foraDaConta.reduce((soma, linha) => soma + (linha.valores[i] || 0), 0));
  const temDinheiroDeFora = totalDeFora.some((v) => Math.abs(v) > 0.005);

  const { reconciliacao } = proposta;
  const naoFecham = (reconciliacao?.conferencias || []).filter((c) => !c.fecha);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Confira o que entendemos da sua planilha
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Lemos a aba <strong>{proposta.aba}</strong> e encontramos{' '}
          {proposta.periodos.length} {proposta.periodos.length === 1 ? 'mês' : 'meses'}
          {' '}({proposta.periodos.map((p) => p.curto).join(', ')}).
          {proposta.escala > 1 && ' Os valores estão declarados em milhares e já foram convertidos.'}
          {proposta.formato === 'balancete' && ' É um balancete: descreve um período só.'}
          {proposta.formato === 'fluxo-de-caixa'
            && ' É um fluxo de caixa: agrupamos os lançamentos por mês e por categoria.'}
        </Typography>
      </Box>

      {!proposta.precisaDeRevisao && (
        <Alert severity="success">
          <AlertTitle>A conta fecha</AlertTitle>
          A soma do que reconhecemos bate com os totais escritos na sua própria planilha, e
          nenhuma linha ficou sem classificação. É só confirmar.
        </Alert>
      )}

      {naoFecham.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>A soma não fechou em {naoFecham.length} bloco(s)</AlertTitle>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {naoFecham.map((c) => (
              <Typography key={c.rotulo} variant="body2">
                <strong>{c.rotulo}</strong>: a planilha diz {formatarMoeda(c.declarado[0])} e o que
                classificamos soma {formatarMoeda(c.somado[0])} no primeiro mês.
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      {proposta.modelo && !proposta.modelo.usado && foraDaConta.length > 0 && (
        <Alert severity="info">
          <AlertTitle>A leitura assistida por IA não entrou nesta importação</AlertTitle>
          Ela resolveria parte das linhas que ficaram sem classificação, e é recurso do plano
          pago com consentimento ativo. O que você confirmar aqui fica guardado: na próxima
          planilha desta empresa, estas linhas já vêm prontas.
        </Alert>
      )}

      {proposta.modelo && proposta.modelo.usado && proposta.modelo.resolveu > 0 && (
        <Alert severity="info">
          <AlertTitle>{proposta.modelo.resolveu} linha(s) foram sugeridas pela IA</AlertTitle>
          Elas aparecem como <strong>provável</strong> e esperam a sua confirmação — sugestão de
          modelo nunca entra como certeza.
        </Alert>
      )}

      {temDinheiroDeFora && (
        <Alert severity="error">
          <AlertTitle>{foraDaConta.length} linha(s) ficariam de fora</AlertTitle>
          Somam {formatarMoeda(totalDeFora[0])} no primeiro mês. Escolha a conta de cada uma
          abaixo, ou confirme sabendo que esse valor não entra no diagnóstico.
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {mostrarTudo
                ? `Todas as ${linhasDeConta.length} linhas`
                : `${precisamDeOlho.length} linha(s) para o seu olho`}
            </Typography>
            <Button size="small" onClick={() => setMostrarTudo((v) => !v)}>
              {mostrarTudo ? 'Mostrar só o que precisa de atenção' : 'Mostrar todas as linhas'}
            </Button>
          </Stack>

          {visiveis.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhuma linha precisa da sua atenção.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Linha da sua planilha</TableCell>
                    <TableCell align="right">{proposta.periodos[0]?.curto}</TableCell>
                    <TableCell>Vira, aqui</TableCell>
                    <TableCell align="center">Como</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiveis.map((linha) => (
                    <TableRow key={linha.indice}>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography variant="body2" noWrap title={linha.rotulo}>
                          {linha.rotulo || '(sem rótulo)'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {linha.valores[0] === null ? '—' : formatarMoeda(linha.valores[0])}
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={contaDaLinha(linha)}
                          onChange={(e) => setConfirmacoes((atual) => ({
                            ...atual, [linha.indice]: e.target.value || null
                          }))}
                        >
                          <MenuItem value="">
                            <em>Não importar esta linha</em>
                          </MenuItem>
                          {opcoes.map((item, i) => (item.separador ? (
                            <MenuItem key={`sep-${i}`} disabled sx={{ opacity: 0.7, fontWeight: 700 }}>
                              {item.rotulo}
                            </MenuItem>
                          ) : (
                            <MenuItem key={item.conta.codigo} value={item.conta.codigo}>
                              {item.conta.nome}
                            </MenuItem>
                          )))}
                        </TextField>
                      </TableCell>
                      <TableCell align="center">
                        {linha.confianca ? (
                          <Chip
                            size="small"
                            label={ROTULO_DA_CONFIANCA[linha.confianca]}
                            color={COR_DA_CONFIANCA[linha.confianca]}
                          />
                        ) : (
                          <Chip size="small" label="não reconhecida" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button onClick={onCancelar} disabled={enviando}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={enviando}
          onClick={() => onConfirmar(confirmacoes)}
        >
          {enviando ? 'Importando…' : 'Confirmar e gerar o diagnóstico'}
        </Button>
      </Stack>
    </Stack>
  );
}

export default ConferenciaImportacao;
